/* eslint complexity: ["error", {"max": 17}] */
import { differenceInCalendarDays, format, isPast, isToday, isTomorrow, isYesterday } from 'date-fns';
import { sv } from 'date-fns/locale';

import type { DateTime } from '../../../../schema/frameworks/entry.js';

type DateFormatBy =
  'd MMMM' | 'PPP' | 'MMMM yyyy' | 'EEEE' | 'EEEE d MMMM' | 'yyyy MM dd' | 'd/M' | 'EEEE, d MMMM yyyy';

export type DateFormatOptions =
  | 'dayAsDateMonthAsText'
  | 'dayAsText'
  | 'dayAsDateAndTextMonthAsText'
  | 'date'
  | 'shortDate'
  | 'dayAsTextCommaDayAsDateMonthAsTextAndYear';

export type TimeFormatOptions = 'hourMinute' | 'hourMinuteSeconds';

export type FormatOptions = 'dateAndTime' | 'timeOnly' | 'dateOnly' | 'dateAndYear' | 'monthAndYear';

export type GetFormattedDateAndTimeOptions = {
  dateFormatOptions?: DateFormatOptions;
  timeFormatOptions?: TimeFormatOptions;
  formatOptions?: FormatOptions;
  shouldUseNameOfDay?: boolean;
  shouldCapitalizeFirstLetter?: boolean;
};

/**
 * Maps DateFormatOptions to date-fns format patterns.
 *
 * @param formatBy - The date format option to convert
 * @returns The corresponding date-fns format pattern
 * @example
 * ```typescript
 * getDateFormatBy('dayAsDateMonthAsText')         // → 'd MMMM'
 * getDateFormatBy('shortDate')                   // → 'd/M'
 * getDateFormatBy('dayAsDateAndTextMonthAsText') // → 'EEEE d MMMM'
 * getDateFormatBy('dayAsText')                   // → 'EEEE'
 * ```
 */
function getDateFormatBy(formatBy: DateFormatOptions): DateFormatBy {
  switch (formatBy) {
    case 'dayAsDateMonthAsText':
      return 'd MMMM';
    case 'shortDate':
      return 'd/M';
    case 'dayAsDateAndTextMonthAsText':
      return 'EEEE d MMMM';
    case 'date':
      return 'yyyy MM dd';
    case 'dayAsText':
      return 'EEEE';
    case 'dayAsTextCommaDayAsDateMonthAsTextAndYear':
      return 'EEEE, d MMMM yyyy';
    default:
      return 'd MMMM';
  }
}

/**
 * Gets the Swedish name of day for a given date, using relative terms when appropriate.
 *
 * @param date - The date to get the name for
 * @param formatBy - The date-fns format pattern to use as fallback
 * @returns Swedish day name ('idag', 'imorgon', 'igår') or formatted date
 * @example
 * ```typescript
 * getNameOfDay(createDateTime(new Date()), 'd MMMM') // → 'idag'
 * getNameOfDay(createDateTime(tomorrow), 'd MMMM')   // → 'imorgon'
 * ```
 */
function getNameOfDay(date: DateTime, formatBy: DateFormatBy): string {
  const daysDifference = differenceInCalendarDays(date, new Date());

  if (isToday(date)) {
    return 'idag';
  }
  if (isTomorrow(date)) {
    return 'imorgon';
  }
  if (isYesterday(date)) {
    return 'igår';
  }
  if (daysDifference > 1 && daysDifference <= 6) {
    return format(date, 'EEEE', { locale: sv });
  }
  if (isPast(date) || Math.abs(differenceInCalendarDays(date, new Date())) > 6) {
    return format(date, formatBy, { locale: sv });
  }
  return format(date, formatBy, { locale: sv });
}

/**
 * Formats a date according to the specified options, with optional Swedish day names.
 *
 * @param options - The formatting options
 * @param options.date - The date to format as DateTime
 * @param options.formatBy - The date format option to use
 * @param options.shouldUseNameOfDay - Whether to use Swedish relative day names
 * @returns The formatted date string
 * @example
 * ```typescript
 * formatDate({ date: createDateTime(new Date()), formatBy: 'dayAsDateMonthAsText' })
 * // → '21 april'
 *
 * formatDate({ date: createDateTime(new Date()), formatBy: 'dayAsDateMonthAsText', shouldUseNameOfDay: true })
 * // → 'idag'
 * ```
 */
function formatDate({
  date,
  formatBy,
  shouldUseNameOfDay,
}: {
  date: DateTime;
  formatBy: DateFormatOptions;
  shouldUseNameOfDay?: boolean;
}): string {
  const dateFormat = getDateFormatBy(formatBy);
  const formattedDate = shouldUseNameOfDay ? getNameOfDay(date, dateFormat) : format(date, dateFormat, { locale: sv });
  return formatBy === 'date' ? formattedDate.replaceAll(' ', '-') : formattedDate;
}

/**
 * Formats time according to the specified time format options.
 *
 * @param options - The formatting options
 * @param options.date - The date to extract time from as DateTime
 * @param options.formatBy - The time format option to use
 * @returns The formatted time string
 * @example
 * ```typescript
 * formatTime({ date: createDateTime(new Date('2026-04-21T14:30:45')), formatBy: 'hourMinute' })
 * // → '14:30'
 *
 * formatTime({ date: createDateTime(new Date('2026-04-21T14:30:45')), formatBy: 'hourMinuteSeconds' })
 * // → '14:30:45'
 * ```
 */
function formatTime({ date, formatBy }: { date: DateTime; formatBy: TimeFormatOptions }): string {
  const hours = format(date, 'HH', { locale: sv });
  const minutes = format(date, 'mm', { locale: sv });
  const seconds = format(date, 'ss', { locale: sv });
  switch (formatBy) {
    case 'hourMinute':
      return `${hours}:${minutes}`;
    case 'hourMinuteSeconds':
      return `${hours}:${minutes}:${seconds}`;
    default:
      return `${hours}:${minutes}`;
  }
}

/**
 * Formats a date and/or time according to Swedish locale and specified options.
 *
 * @param date - The date to format as DateTime
 * @param options - The formatting options
 * @param options.dateFormatOptions - How to format the date part (default: 'dayAsDateMonthAsText')
 * @param options.timeFormatOptions - How to format the time part (default: 'hourMinute')
 * @param options.formatOptions - What parts to include: 'dateOnly', 'timeOnly', 'dateAndTime', 'dateAndYear', or 'monthAndYear'
 * @param options.shouldUseNameOfDay - Whether to use Swedish relative day names like 'idag', 'imorgon' (default: false)
 * @param options.shouldCapitalizeFirstLetter - Whether to capitalize the first letter (default: false)
 * @returns The formatted date/time string in Swedish
 * @example
 * ```typescript
 * const date = createDateTime(new Date('2026-04-21T14:30:00'))
 *
 * // Date only
 * getFormattedDateAndTime(date, { formatOptions: 'dateOnly' })
 * // → '21 april'
 *
 * // Time only
 * getFormattedDateAndTime(date, { formatOptions: 'timeOnly' })
 * // → '14:30'
 *
 * // Date and time
 * getFormattedDateAndTime(date, { formatOptions: 'dateAndTime' })
 * // → '21 april 14:30'
 *
 * // Date with year
 * getFormattedDateAndTime(date, { formatOptions: 'dateAndYear' })
 * // → '21 april 2026'
 *
 * // Month and year only
 * getFormattedDateAndTime(date, { formatOptions: 'monthAndYear' })
 * // → 'april 2026'
 *
 * // With Swedish day names
 * getFormattedDateAndTime(createDateTime(new Date()), {
 *   formatOptions: 'dateOnly',
 *   shouldUseNameOfDay: true,
 *   shouldCapitalizeFirstLetter: true
 * })
 * // → 'Idag'
 *
 * // Short date format
 * getFormattedDateAndTime(date, {
 *   formatOptions: 'dateOnly',
 *   dateFormatOptions: 'shortDate'
 * })
 * // → '21/4'
 *
 * // Full day name with date and month
 * getFormattedDateAndTime(date, {
 *   formatOptions: 'dateOnly',
 *   dateFormatOptions: 'dayAsDateAndTextMonthAsText'
 * })
 * // → 'tisdag 21 april'
 * ```
 */
export function getFormattedDateAndTime(date: DateTime, options?: GetFormattedDateAndTimeOptions): string {
  const dateFormatOption = options?.dateFormatOptions ?? 'dayAsDateMonthAsText';
  const timeFormatOption = options?.timeFormatOptions ?? 'hourMinute';
  const shouldUseNameOfDay = options?.shouldUseNameOfDay ?? false;
  const shouldCapitalizeFirstLetter = options?.shouldCapitalizeFirstLetter ?? false;

  let formattedDate: string;
  switch (options?.formatOptions) {
    case 'dateOnly': {
      formattedDate = formatDate({ date, formatBy: dateFormatOption, shouldUseNameOfDay: shouldUseNameOfDay });
      break;
    }
    case 'timeOnly': {
      formattedDate = formatTime({ date, formatBy: timeFormatOption });
      break;
    }
    case 'dateAndTime': {
      const datePart = formatDate({ date, formatBy: dateFormatOption, shouldUseNameOfDay: shouldUseNameOfDay });
      const timePart = formatTime({ date, formatBy: timeFormatOption });
      formattedDate = `${datePart} ${timePart}`;
      break;
    }
    case 'dateAndYear': {
      // Since formatOption 'dateAndYear' includes the year, we need to ensure year is not added twice.
      formattedDate = `${formatDate({ date, formatBy: dateFormatOption, shouldUseNameOfDay: shouldUseNameOfDay })} ${dateFormatOption === 'date' ? '' : format(date, 'yyyy', { locale: sv })}`;
      break;
    }
    case 'monthAndYear': {
      formattedDate = format(date, 'MMMM yyyy', { locale: sv });
      break;
    }
    default: {
      formattedDate = formatDate({ date, formatBy: dateFormatOption, shouldUseNameOfDay: shouldUseNameOfDay });
    }
  }

  if (shouldCapitalizeFirstLetter) {
    formattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  }
  return formattedDate;
}

/**
 * Formats a date into an accessible Swedish string for screen readers.
 *
 * Prefer rendering dates with a semantic `&lt;time dateTime="…">` element so the
 * browser and assistive tech get a machine-readable timestamp:
 *
 *     &lt;time dateTime="2024-01-01T20:00">21 april 14:30&lt;/time>
 *
 * Reach for `aria-label` (using the string returned by this function) only when
 * the visible text is too compact to be understood out of context — e.g. when
 * the UI shows "21/4 14:30" but you want screen readers to announce
 * "tisdag 21 april klockan 14:30". Don't duplicate the visible text in
 * `aria-label`; it overrides it for assistive tech.
 *
 * @param date - The date to format as DateTime
 * @returns The formatted Swedish date and time string
 * @example
 * ```typescript
 * // Today/yesterday/tomorrow → "idag 3 juni klockan 16:27"
 * // Within the next 6 days → "torsdag 8 juni klockan 14:15"
 * // Same year (otherwise) → "tisdag 21 juni klockan 16:00"
 * // Different year → "måndag 2 juni 2027 klockan 16:45"
 * ```
 */
export function getAccessibleDateAndTime(date: DateTime): string {
  const time = format(date, 'HH:mm', { locale: sv });
  const daysDifference = differenceInCalendarDays(date, new Date());

  if (isToday(date) || isYesterday(date) || isTomorrow(date)) {
    let relative: string;
    if (isToday(date)) {
      relative = 'idag';
    } else if (isTomorrow(date)) {
      relative = 'imorgon';
    } else {
      relative = 'igår';
    }
    const datePart = format(date, 'd MMMM', { locale: sv });
    return `${relative} ${datePart} klockan ${time}`;
  }

  if (daysDifference > 1 && daysDifference <= 6) {
    const weekday = format(date, 'EEEE', { locale: sv });
    const datePart = format(date, 'd MMMM', { locale: sv });
    return `${weekday} ${datePart} klockan ${time}`;
  }

  const isSameYear = format(date, 'yyyy', { locale: sv }) === format(new Date(), 'yyyy', { locale: sv });
  const datePart = isSameYear
    ? format(date, 'EEEE d MMMM', { locale: sv })
    : format(date, 'EEEE d MMMM yyyy', { locale: sv });
  return `${datePart} klockan ${time}`;
}
